/**
 * Shared timesheet types
 *
 * Centralized type definitions used across timesheet components:
 * TimesheetList, TimesheetWeek, TimesheetAdminReview, etc.
 */

export interface TimesheetEntry {
  id?: number;
  matter: number;
  /**
   * Optional per-entry phase override. null/undefined = automatic
   * attribution by date. Only settable when the matter has ≥ 2 phases.
   */
  phase?: number | null;
  description: string;
  hours: string;
  billable: boolean;
  date: string;
  user?: number;
  isNew?: boolean;
  timer_active?: boolean;
  timer_start?: string;
  matterName?: string;
  reviewed?: boolean;
  locked?: boolean;
  invoiced?: boolean;
  /**
   * The post-write-down billable amount. Admin/Accounting/Superuser only;
   * stripped from the API response for every other role. Use
   * `theoretical_billable_hours` for the worker-facing recorded number.
   */
  billable_hours?: number | string;
  /**
   * The raw billable hours as recorded by the worker / timer. Visible to
   * every role. This is the value normal users should see in their UI.
   */
  theoretical_billable_hours?: number | string;
  fee?: number;
  fixed_fee?: number;
  rate?: number | null;
  net_rate?: number | null;
  created_at?: string;
  updated_at?: string;
}

/**
 * Read the billable-hours value to display: prefer the post-write-down
 * `billable_hours` if present (admin view), else fall back to
 * `theoretical_billable_hours` (worker view), else 0.
 */
export function getDisplayBillableHours(
  entry: Pick<TimesheetEntry, 'billable_hours' | 'theoretical_billable_hours'> | null | undefined,
): number {
  if (!entry) return 0;
  const adjusted = entry.billable_hours;
  if (adjusted !== undefined && adjusted !== null && adjusted !== '') {
    const n = typeof adjusted === 'string' ? parseFloat(adjusted) : adjusted;
    if (!Number.isNaN(n)) return n;
  }
  const theoretical = entry.theoretical_billable_hours;
  if (theoretical !== undefined && theoretical !== null && theoretical !== '') {
    const n = typeof theoretical === 'string' ? parseFloat(theoretical) : theoretical;
    if (!Number.isNaN(n)) return n;
  }
  return 0;
}

export interface FilterState {
  userIds: number[];
  matterIds: number[];
  dateFrom: string | null;
  dateTo: string | null;
  billable: boolean | null;
  reviewed: boolean | null;
  invoiced: boolean | null;
  locked: boolean | null;
  sortBy: string[];
}

export const DEFAULT_FILTER_STATE: FilterState = {
  userIds: [],
  matterIds: [],
  dateFrom: null,
  dateTo: null,
  billable: null,
  reviewed: null,
  invoiced: null,
  locked: null,
  sortBy: ['-date'],
};

/** Auto-resize a textarea to fit its content */
export function autoResizeTextarea(textarea: HTMLTextAreaElement): void {
  if (textarea) {
    textarea.style.height = 'auto';
    textarea.style.height = `${Math.max(56, textarea.scrollHeight + 4)}px`;
  }
}
